import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { twMerge } from 'tailwind-merge';

import TableStakeInfo from '@/components/TableStakeInfo/TableStakeInfo';
import { TetherIconColorOptions } from '@/components/CurrencySymbolMask/CurrencyIcon';
import TablePlay from '@/features/poker-table/TablePlay';
import useIsMobileView from '@/hooks/useIsMobileView';
import { AuthState } from '@/services/auth/authState';
import DoubleLoginModal from '@/components/DoubleLoginModal/DoubleLoginModal';
import Concierge from '../../play/elements/Concierge';
import UserActions from '../../play/elements/UserActions';
import TableStreams, { StreamType } from '../../play/elements/TableStreams/TableStreams';

import {
  selectAnte,
  selectBigBet,
  selectBigBlind,
  selectGame,
  selectHandState,
  selectIsTableFull,
  selectPlayerSeat,
  selectSmallBet,
  selectSmallBlind,
  selectTableState,
  selectTableUrl,
  selectWaitlist,
} from '../../../../services/table/tableSelectors';
import ActionButton, { ActionButtonVariant } from '../ActionButton/ActionButton';
import SitBackIn from '../SitBackIn/SitBackIn';
import TableHeader from '../TableHeader/TableHeader';
import TableSounds from '../TableSounds/TableSounds';
import TableModal from '../TableModal/TableModal';
import styles from './Table.module.css';

interface TableProps {
  canWaitForBigBlind: boolean;
  isSitout: boolean;
  onPostNextHand: () => void;
  onWaitForBigBlind: () => void;
  onSitIn: () => Promise<void>;
  tableId: string;
  userId: string;
  user: AuthState;
  waitingForBigBlind: boolean;
}

const Table: React.FC<TableProps> = ({
  canWaitForBigBlind,
  isSitout,
  onPostNextHand,
  onWaitForBigBlind,
  onSitIn,
  tableId,
  userId,
  user,
  waitingForBigBlind,
}) => {
  const isMobileView = useIsMobileView({ checkForUserAgent: true });
  const prevIsMobileViewRef = useRef(isMobileView);
  const [showChat, setShowChat] = useState<boolean>(!isMobileView);
  const [showTable, setShowTable] = useState<boolean>(true);
  const [streamType, setStreamType] = useState<StreamType>(StreamType.CHAT);

  const tableState = useSelector(selectTableState(tableId));
  const tableUrl = useSelector(selectTableUrl(tableId));
  const playerSeat = useSelector(selectPlayerSeat(tableId));
  const game = useSelector(selectGame(tableId));
  const ante = useSelector(selectAnte(tableId));
  const smallBlind = useSelector(selectSmallBlind(tableId));
  const bigBlind = useSelector(selectBigBlind(tableId));
  const smallBet = useSelector(selectSmallBet(tableId));
  const bigBet = useSelector(selectBigBet(tableId));
  const isTableFull = useSelector(selectIsTableFull(tableId));
  const waitlist = useSelector(selectWaitlist(tableId));
  const handState = useSelector(selectHandState(tableId));

  useEffect(() => {
    setShowTable(!(isMobileView && showChat));
  }, [isMobileView, showChat]);
  useEffect(() => {
    const prevIsMobileView = prevIsMobileViewRef.current;

    if (isMobileView !== prevIsMobileView) {
      setShowChat(!isMobileView);
    }

    prevIsMobileViewRef.current = isMobileView;
  }, [isMobileView]);

  // const handleBeforeUnload = (event: BeforeUnloadEvent) => {
  //   event.preventDefault();
  //   dispatch(leaveTableAttemptAction(tableId));
  // };

  // useEffect(() => {
  //   if (tableState?.modal?.type !== undefined) {
  //     window.onbeforeunload = null;
  //   } else {
  //     window.onbeforeunload = handleBeforeUnload;
  //   }
  // });

  return (
    <div className={`${styles.container} flex min-h-dvh flex-col`}>
      <TableHeader
        selectedTableId={tableId}
        userId={userId}
        showChat={showChat}
        setShowChat={setShowChat}
        game={game}
        stakes={
          <TableStakeInfo
            fontSize="sm"
            color={TetherIconColorOptions.white}
            ante={ante}
            smallBlind={smallBlind}
            bigBlind={bigBlind}
            smallBet={smallBet}
            bigBet={bigBet}
          />
        }
        playerSeat={playerSeat}
        tableId={tableId}
        tableTabsInDesktopEnabled={Boolean(user.settings?.tableTabsInDesktopEnabled)}
      />
      <div className={`${styles.tableContainer} z-10 flex min-h-0 flex-grow items-center justify-center`}>
        {showTable && tableState && <TablePlay tableState={tableState} />}
      </div>

      <div className={twMerge(styles.actionAndChatContainer, 'flex min-h-0 pt-8', isMobileView && 'z-20')}>
        <div
          className={` ${styles.chatStreamContainer} ${showChat ? styles.show : styles.hide} ${isMobileView && showChat ? styles.mobileActive : ''} `}
        >
          <TableStreams
            tableId={tableId}
            userId={userId}
            streamType={streamType}
            setStreamType={setStreamType}
            onCloseChat={() => setShowChat(!showChat)}
          />
        </div>
        <div className={twMerge(styles.actionContainer)}>
          {/* Always render this container, but conditionally render its contents */}
          {!playerSeat?.leftTable && (
            <>
              {isSitout && !(showChat && isMobileView) && (
                <div className="flex-1">
                  <SitBackIn onClick={onSitIn} />
                </div>
              )}

              {canWaitForBigBlind && (
                <div className="flex w-auto flex-row justify-center gap-4">
                  <ActionButton
                    variant={
                      !waitingForBigBlind
                        ? ActionButtonVariant.WaitForBigBlindActive
                        : ActionButtonVariant.WaitForBigBlindInactive
                    }
                    isChecked={!waitingForBigBlind}
                    onClick={onPostNextHand}
                    isPlayerTurn={false}
                    className="!w-full !min-w-40 !border-solid text-[16px]"
                    containerClassName="flex justify-center"
                  >
                    {playerSeat?.skippedBB ? 'Post BB+SB' : playerSeat?.skippedSB ? 'Post SB (Dead)' : 'Post Next Hand'}
                  </ActionButton>
                  <ActionButton
                    variant={
                      waitingForBigBlind
                        ? ActionButtonVariant.WaitForBigBlindActive
                        : ActionButtonVariant.WaitForBigBlindInactive
                    }
                    isChecked={!!waitingForBigBlind}
                    onClick={onWaitForBigBlind}
                    isPlayerTurn={false}
                    className="!w-full !min-w-44 !border-solid text-[16px]"
                    containerClassName="flex justify-center"
                  >
                    Wait for Big Blind
                  </ActionButton>
                </div>
              )}
              {!playerSeat && tableUrl && ((isMobileView && !showChat) || !isMobileView) && (
                <Concierge tableId={tableId} userId={userId} fullTable={isTableFull} waitlist={waitlist} />
              )}

              {handState?.state &&
                ((playerSeat?.availableActions && playerSeat?.active) ||
                  (playerSeat?.preActionsAvailable && playerSeat?.active)) && <UserActions tableId={tableId} />}
            </>
          )}
        </div>
      </div>
      {user.settings?.soundEnabled && <TableSounds tableId={tableId} />}
      <TableModal />
      <DoubleLoginModal />
    </div>
  );
};

export default Table;
